/**
 * What a person's character does when it is clicked or right-clicked, on whichever scene the page
 * draws — the room or the Bug Hunt.
 *
 * Click your own character and a box opens where you stand: what you type is said in the room, a
 * bubble over your head for everybody looking. Right-click anybody for a menu: speak, send your
 * agent a prompt (your own agents only — remote control is nobody else's), change your own character
 * (in a window, without leaving the room), or open their day.
 *
 * Worked out from the DOM, like the mosquito controls: a station carries `data-drop-developer`.
 * A press that moved is a drag, not a click, and opens nothing.
 *
 * Your own character's menu also walks it to another area of the office (bughunt/office.ts): the
 * hall, the kitchen or the war room. An agent standing in the hall for somebody who walked out is
 * that person's, for the menu as for everything else (`ownerOf`).
 */

import { useCallback, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'
import { useI18n } from '../lib/i18n'
import { PersonMenu, PromptBox, RoomNote, SayBox } from '../components/PersonMenu'
import { Modal } from '../components/Modal'
import { MyCharacterEditor } from '../pages/MyCharacter'
import { AreaMenuItems } from './OfficeNav'
import type { RoomArea } from '../lib/types'

const MOVED = 5

type Open =
  | { kind: 'menu'; developerId: string; x: number; y: number }
  | { kind: 'say'; x: number; y: number }
  | { kind: 'prompt'; x: number; y: number }
  | { kind: 'character' }

function stationUnder(event: { target: EventTarget }): string | null {
  const target = event.target as Element
  // A mosquito sits in front of people; it has its own controls.
  if (target.closest?.('[data-bug]')) return null
  return target.closest?.('[data-drop-developer]')?.getAttribute('data-drop-developer') ?? null
}

export function usePersonControls({
  meDeveloperId,
  nameOf,
  onSay,
  ownerOf,
  moveTo,
}: {
  /** The signed-in person's own character, if they have one in the room. */
  meDeveloperId: string | null | undefined
  nameOf: (developerId: string) => string
  onSay: (text: string) => Promise<unknown>
  /** Whose a figure is, when it is not a person's own (an agent standing in for them). */
  ownerOf?: (id: string) => string | null | undefined
  /** Walks your own character to an area: where it is now, and the move. */
  moveTo?: { current?: RoomArea; go: (area: RoomArea) => Promise<unknown> }
}): {
  handlers: {
    onPointerDownCapture: (event: ReactPointerEvent) => void
    onClickCapture: (event: ReactMouseEvent) => void
    onContextMenu: (event: ReactMouseEvent) => void
  }
  overlay: ReactNode
} {
  const { t } = useI18n()
  const [open, setOpen] = useState<Open | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const pressedAt = useRef<{ x: number; y: number } | null>(null)

  const close = useCallback(() => setOpen(null), [])

  const onPointerDownCapture = useCallback((event: ReactPointerEvent) => {
    pressedAt.current = { x: event.clientX, y: event.clientY }
  }, [])

  const resolve = useCallback((id: string | null) => (id ? ownerOf?.(id) ?? id : null), [ownerOf])

  const onClickCapture = useCallback(
    (event: ReactMouseEvent) => {
      const id = resolve(stationUnder(event))
      if (!id || !meDeveloperId || id !== meDeveloperId) return
      const from = pressedAt.current
      if (from && Math.hypot(event.clientX - from.x, event.clientY - from.y) > MOVED) return
      // Selection still happens underneath; this only adds the box.
      setOpen({ kind: 'say', x: event.clientX - 140, y: event.clientY - 64 })
    },
    [meDeveloperId, resolve],
  )

  const onContextMenu = useCallback((event: ReactMouseEvent) => {
    if (event.defaultPrevented) return
    const id = resolve(stationUnder(event))
    if (!id) return
    event.preventDefault()
    setOpen({ kind: 'menu', developerId: id, x: event.clientX, y: event.clientY })
  }, [resolve])

  const flash = useCallback((text: string) => {
    setNote(text)
    window.setTimeout(() => setNote(null), 4000)
  }, [])

  const overlay = (
    <>
      {open?.kind === 'menu' && (
        <PersonMenu
          name={nameOf(open.developerId)}
          developerId={open.developerId}
          isMe={open.developerId === meDeveloperId}
          x={open.x}
          y={open.y}
          onSay={() => setOpen({ kind: 'say', x: open.x, y: open.y })}
          onPrompt={() => setOpen({ kind: 'prompt', x: open.x, y: open.y })}
          onCharacter={() => setOpen({ kind: 'character' })}
          onClose={close}
        >
          {open.developerId === meDeveloperId && moveTo && (
            <AreaMenuItems
              current={moveTo.current}
              onMove={(area) => {
                close()
                moveTo.go(area).catch(() => flash(t('office.moveFailed')))
              }}
            />
          )}
        </PersonMenu>
      )}
      {open?.kind === 'say' && <SayBox x={open.x} y={open.y} onSay={onSay} onClose={close} />}
      {open?.kind === 'prompt' && <PromptBox x={open.x} y={open.y} onClose={close} onSent={() => flash(t('room.prompt.sent'))} />}
      {open?.kind === 'character' && (
        <Modal title={t('character.title')} onClose={close} wide>
          <MyCharacterEditor onSaved={() => { close(); flash(t('character.saved')) }} onCancel={close} />
        </Modal>
      )}
      {note && <RoomNote>{note}</RoomNote>}
    </>
  )

  return { handlers: { onPointerDownCapture, onClickCapture, onContextMenu }, overlay }
}

/**
 * The mosquito controls and the person controls on one wrapper. A right-click on a mosquito is the
 * mosquito's — its handler claims the event, and the person's then leaves it alone.
 */
export function combineControls(
  bug: { onPointerDownCapture: (event: ReactPointerEvent) => void; onContextMenu: (event: ReactMouseEvent) => void },
  person: ReturnType<typeof usePersonControls>['handlers'],
) {
  return {
    onPointerDownCapture: (event: ReactPointerEvent) => {
      bug.onPointerDownCapture(event)
      person.onPointerDownCapture(event)
    },
    onClickCapture: person.onClickCapture,
    onContextMenu: (event: ReactMouseEvent) => {
      bug.onContextMenu(event)
      person.onContextMenu(event)
    },
  }
}
